import { z } from 'zod'

/**
 * Task templates — the shape of the six JSON columns on `task_templates`.
 *
 * LINEAGE
 * -------
 * This contract is a deliberate extension of `CycleGroupTemplateSchema.tasks`
 * (./cycle.ts), NOT a new invention. That shape is the only zod-defined,
 * production-exercised "task written down as JSON" in the codebase — it is
 * consumed by cycle.service.ts / recurring-cycle.service.ts and authored by
 * apps/web/src/components/cycles/CycleTemplateSettings.tsx.
 *
 * The other candidate, `ProjectTemplate.groups[].initialTasks`
 * (apps/api/src/templates/project-template.service.ts), was rejected as the
 * base: it has no schema anywhere, and it stores ABSOLUTE dates
 * (`dueDate`, `scheduledFor`) which are meaningless for a template applied at
 * an arbitrary future moment. Two ideas ARE borrowed from it, noted inline:
 * the `statusSource: 'inherit'` concept, and the `order: 'a1'` default.
 *
 * Field names kept verbatim from the cycle shape:
 *   id, name, description, priority, estimatedHours, dueDaysOffset, order,
 *   assigneeIds, tags, customFields
 *
 * Deliberate deviations, each with a reason:
 *
 * 1. `dueCalculationType` is DROPPED. Cycles have a period (periodStart /
 *    periodEnd) so RELATIVE_START vs RELATIVE_END is a real choice. A task
 *    template is applied against a single caller-supplied anchor date, so
 *    every mode collapses to "anchor + N days". "No due date at all" is
 *    encoded as `dueDaysOffset: null` rather than by adding a mode.
 *
 * 2. `subtasks` is ADDED (cycles are flat: group → tasks). Subtask trees are
 *    the entire point of a task template — "client onboarding" is a parent
 *    task with a shaped set of children. Nesting is expressed by explicit
 *    composition rather than `z.lazy()`, for two reasons: the shared index
 *    warns that `z.lazy()` re-exports trigger Hermes "property is not
 *    configurable" errors on mobile (see types/index.ts and task/subtask.ts),
 *    and an unbounded tree has no natural apply-time recursion guard. The
 *    depth is capped at MAX_TASK_TEMPLATE_DEPTH (3 levels below the root).
 */

// ---------------------------------------------------------------------------
// Depth
// ---------------------------------------------------------------------------

/**
 * Levels of subtask nesting a template may declare below its root task.
 * Root task = Task.depth 0, so the deepest materialized task is depth 3.
 */
export const MAX_TASK_TEMPLATE_DEPTH = 3

// ---------------------------------------------------------------------------
// Node (one task in the template tree)
// ---------------------------------------------------------------------------

const TaskTemplateNodeFields = {
  /**
   * Stable identifier within the template document. Client-generated (uuid or
   * nanoid), NOT a Task id — the same convention as CycleGroupTemplate.tasks[].id
   * and ProjectTemplate.groups[].id.
   */
  id: z.string().min(1),
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  /** TaskPriorityEnum value. Left as a string for parity with the cycle shape. */
  priority: z.string().nullable().optional(),
  estimatedHours: z.number().nullable().optional(),
  /**
   * Days from the apply-time anchor date. `null` means "leave this task
   * undated" — templates for reactive work (support runbooks) genuinely want
   * no due date, and 0 already means "due on the anchor day".
   */
  dueDaysOffset: z.number().int().nullable().default(null),
  /** Fractional-index order key, same default as the cycle + project shapes. */
  order: z.string().default('a1'),
  /**
   * User ids. May be empty — the apply call can supply a fallback assignee,
   * which is the common case for a template shared across an org.
   */
  assigneeIds: z.array(z.string()).default([]),
  tags: z.array(z.string()).default([]),
  customFields: z.record(z.any()).default({}),
}

/** Depth 3 — the floor. Carries no `subtasks`, which is what bounds the tree. */
export const TaskTemplateLeafNodeSchema = z.object(TaskTemplateNodeFields)
export type TaskTemplateLeafNode = z.infer<typeof TaskTemplateLeafNodeSchema>

/** Depth 2. */
export const TaskTemplateBranchNodeSchema = z.object({
  ...TaskTemplateNodeFields,
  subtasks: z.array(TaskTemplateLeafNodeSchema).default([]),
})
export type TaskTemplateBranchNode = z.infer<
  typeof TaskTemplateBranchNodeSchema
>

/** Depth 1 — the direct children of the root task, i.e. `subtaskTemplates[]`. */
export const TaskTemplateNodeSchema = z.object({
  ...TaskTemplateNodeFields,
  subtasks: z.array(TaskTemplateBranchNodeSchema).default([]),
})
export type TaskTemplateNode = z.infer<typeof TaskTemplateNodeSchema>

/**
 * A node at any level, for code that walks the tree without caring how deep it
 * currently is. Structural only — parse with the level-specific schema.
 */
export type AnyTaskTemplateNode = TaskTemplateLeafNode & {
  subtasks?: AnyTaskTemplateNode[]
}

// ---------------------------------------------------------------------------
// Column: subtask_templates
// ---------------------------------------------------------------------------

export const TaskTemplateSubtasksSchema = z
  .array(TaskTemplateNodeSchema)
  .default([])

// ---------------------------------------------------------------------------
// Column: default_fields (NOT NULL)
// ---------------------------------------------------------------------------

/**
 * Defaults for the ROOT task the template produces. Same field vocabulary as a
 * node, minus `id`/`order` (the root has no siblings inside the template) and
 * with `name` optional — when omitted the root task takes the template's name,
 * which is what "Client onboarding" should produce by default.
 */
export const TaskTemplateDefaultFieldsSchema = z.object({
  name: z.string().max(255).nullable().optional(),
  description: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  estimatedHours: z.number().nullable().optional(),
  dueDaysOffset: z.number().int().nullable().default(null),
  assigneeIds: z.array(z.string()).default([]),
  tags: z.array(z.string()).default([]),
  customFields: z.record(z.any()).default({}),
  /** Applied to the root task only; subtasks of a private task stay internal. */
  isPrivate: z.boolean().default(false),
})
export type TaskTemplateDefaultFields = z.infer<
  typeof TaskTemplateDefaultFieldsSchema
>

// ---------------------------------------------------------------------------
// Column: status_config (NOT NULL)
// ---------------------------------------------------------------------------

/**
 * Borrowed directly from ProjectTemplate groups, which carry
 * `statusSource: 'inherit' | 'custom'` — same literal, same lowercase.
 *
 * 'inherit' (the default and the overwhelmingly common case) means: resolve the
 * status from the destination the way any freshly created task would, via
 * TaskStatusResolutionService. 'explicit' pins a status id, for templates that
 * should always land in e.g. "Waiting on client".
 *
 * There is no 'custom' option: a task template cannot define a status SET the
 * way a project template can, because it does not own the board it lands on.
 */
export const TaskTemplateStatusConfigSchema = z.object({
  statusSource: z.enum(['inherit', 'explicit']).default('inherit'),
  /** Status id, only meaningful when statusSource === 'explicit'. */
  status: z.string().nullable().optional(),
  /** TaskStatusTypeEnum value that pairs with `status`. */
  statusTypeId: z.string().nullable().optional(),
})
export type TaskTemplateStatusConfig = z.infer<
  typeof TaskTemplateStatusConfigSchema
>

// ---------------------------------------------------------------------------
// Column: checklists
// ---------------------------------------------------------------------------

/**
 * A checklist is a flat tick-list attached to the root task.
 *
 * Scrambl has NO checklist storage on Task — the only checklist-shaped JSON in
 * the schema belongs to Deployment (`[{ id, label, done, doneBy, doneAt }]`),
 * and "Checklist" is literally the personal-mode label the task sheet uses for
 * subtasks (TaskInlineDetailContent.tsx). So a checklist that persisted as its
 * own structure would render nowhere.
 *
 * Therefore checklists are modelled as an AUTHORING affordance whose output is
 * real tasks: at apply time each checklist becomes one subtask named `title`
 * under the root, with each item as a leaf subtask beneath it. The item shape
 * is DeploymentChecklistItem minus the completion fields (`done`/`doneBy`/
 * `doneAt`), which belong to the instantiated task, not to the template.
 *
 * The split from `subtaskTemplates` is ergonomic, not structural: the tree
 * editor is for work that carries priority/estimate/assignee/dates, the
 * checklist editor is for pure tick-lists typed in one go.
 */
export const TaskTemplateChecklistItemSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1).max(255),
})
export type TaskTemplateChecklistItem = z.infer<
  typeof TaskTemplateChecklistItemSchema
>

export const TaskTemplateChecklistSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1).max(255),
  items: z.array(TaskTemplateChecklistItemSchema).default([]),
})
export type TaskTemplateChecklist = z.infer<typeof TaskTemplateChecklistSchema>

// ---------------------------------------------------------------------------
// Columns: dependencies, triggers — RESERVED
// ---------------------------------------------------------------------------

/**
 * RESERVED. Both columns are validated as "must be empty" rather than given a
 * speculative shape.
 *
 * `dependencies`: TaskDependency rows exist and template-declared edges
 * (node id → node id, resolved to task ids after materialization) are a real
 * future feature — but nothing in the apply path creates them today and no
 * surface authors them, so a shape now would be fiction. When it ships, the
 * natural form is `{ fromNodeId, toNodeId, type }[]` mirroring TaskDependency.
 *
 * `triggers`: there is no template-side automation entry point at all; workflow
 * automation already owns "when X happens, do Y" and a second trigger system on
 * templates would be a competing mechanism, not an extension of one.
 *
 * Writing a non-empty value is rejected at the API boundary, so no caller can
 * accumulate data in a shape we would then have to honor.
 */
export const ReservedTaskTemplateSlotSchema = z
  .array(z.unknown())
  .max(0, 'This field is reserved and must be empty')
  .default([])

// ---------------------------------------------------------------------------
// The template record
// ---------------------------------------------------------------------------

export const TaskTemplateSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid().nullable(),
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  defaultFields: TaskTemplateDefaultFieldsSchema,
  statusConfig: TaskTemplateStatusConfigSchema,
  subtaskTemplates: TaskTemplateSubtasksSchema,
  dependencies: ReservedTaskTemplateSlotSchema,
  checklists: z.array(TaskTemplateChecklistSchema).default([]),
  triggers: ReservedTaskTemplateSlotSchema,
  category: z.string().max(100).nullable().optional(),
  tags: z.array(z.string()).default([]),
  /** Seeded org-agnostic templates. Read-only to org users. */
  isSystem: z.boolean().default(false),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})
export type TaskTemplate = z.infer<typeof TaskTemplateSchema>

/** List rows carry a cheap summary instead of the full tree. */
export const TaskTemplateSummarySchema = TaskTemplateSchema.omit({
  subtaskTemplates: true,
  checklists: true,
  dependencies: true,
  triggers: true,
}).extend({
  subtaskCount: z.number().int().default(0),
  checklistItemCount: z.number().int().default(0),
})
export type TaskTemplateSummary = z.infer<typeof TaskTemplateSummarySchema>

export const CreateTaskTemplateSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(5000).nullable().optional(),
  defaultFields: TaskTemplateDefaultFieldsSchema.default({}),
  statusConfig: TaskTemplateStatusConfigSchema.default({}),
  subtaskTemplates: TaskTemplateSubtasksSchema,
  checklists: z.array(TaskTemplateChecklistSchema).default([]),
  dependencies: ReservedTaskTemplateSlotSchema.optional(),
  triggers: ReservedTaskTemplateSlotSchema.optional(),
  category: z.string().max(100).nullable().optional(),
  tags: z.array(z.string()).default([]),
})
export type CreateTaskTemplate = z.infer<typeof CreateTaskTemplateSchema>

export const UpdateTaskTemplateSchema = CreateTaskTemplateSchema.partial()
export type UpdateTaskTemplate = z.infer<typeof UpdateTaskTemplateSchema>

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

/**
 * What the CALLER supplies (destination + when + who) versus what the TEMPLATE
 * fixes (shape + relative timing + per-node fields).
 *
 * Caller: moduleType/moduleId, groupId, sprintId, anchorDate, assigneeIds
 * fallback, and a root-name override. These are the things that cannot be known
 * when the template is written — a "monthly reporting" template has no opinion
 * about which client's project it runs in this month.
 *
 * Template: the subtask tree and its parent wiring, per-node priority /
 * estimate / tags / custom fields, dueDaysOffset relative to the anchor, and
 * the status policy. Overriding those per-apply would make the template
 * meaningless, so the API deliberately does not accept them.
 */
export const ApplyTaskTemplateSchema = z.object({
  /** ModuleTypeEnum value — PROJECT, MEMBER, ENTITY, … */
  moduleType: z.string().min(1),
  moduleId: z.string().uuid(),
  groupId: z.string().uuid().nullable().optional(),
  sprintId: z.string().uuid().nullable().optional(),
  /** Overrides the root task name (and only the root's). */
  name: z.string().min(1).max(255).optional(),
  /**
   * Every `dueDaysOffset` is measured from this date. Defaults to today in the
   * server's clock when omitted.
   */
  anchorDate: z.coerce.date().nullable().optional(),
  /**
   * Fallback assignees, used for any node whose template `assigneeIds` is
   * empty. Nodes that name their own assignees keep them — a template that
   * says "legal reviews the MSA" should not be silently re-pointed.
   */
  assigneeIds: z.array(z.string().uuid()).default([]),
})
export type ApplyTaskTemplate = z.infer<typeof ApplyTaskTemplateSchema>

export const ApplyTaskTemplateResultSchema = z.object({
  rootTaskId: z.string().uuid(),
  rootTaskNumber: z.string().nullable(),
  /** Root included. */
  createdTaskCount: z.number().int(),
  createdTaskIds: z.array(z.string().uuid()),
})
export type ApplyTaskTemplateResult = z.infer<
  typeof ApplyTaskTemplateResultSchema
>

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Total tasks a template will materialize, root included. */
export function countTaskTemplateNodes(template: {
  subtaskTemplates?: AnyTaskTemplateNode[] | null
  checklists?: TaskTemplateChecklist[] | null
}): number {
  const walk = (nodes: AnyTaskTemplateNode[] | null | undefined): number =>
    (nodes ?? []).reduce((total, node) => total + 1 + walk(node.subtasks), 0)

  const checklistTasks = (template.checklists ?? []).reduce(
    (total, checklist) => total + 1 + checklist.items.length,
    0,
  )

  return 1 + walk(template.subtaskTemplates) + checklistTasks
}

/** anchor + offset days, or null when the node is deliberately undated. */
export function resolveTemplateDueDate(
  anchorDate: Date,
  dueDaysOffset: number | null | undefined,
): Date | null {
  if (dueDaysOffset === null || dueDaysOffset === undefined) return null
  const due = new Date(anchorDate)
  due.setDate(due.getDate() + dueDaysOffset)
  return due
}
